import { useCallback, useState } from 'react';

import { useSetLessonAttendanceMutation } from 'core/graphql';

/**
 * Поштучное сохранение отметки (ТЗ 0026).
 *
 * Оптимистичный ответ формируется под тот же `__typename`, что отдаёт
 * `groupLessonAttendanceForLms` (`LessonAttendancesType`) и с тем же `id` —
 * Apollo нормализует сущность и сам обновляет список журнала. Поэтому здесь
 * нет ни `cache.modify`, ни `refetchQueries`: при ошибке Apollo так же сам
 * откатывает оптимистику, и строка возвращается в прежнее состояние.
 */
export const useSetAttendance = () => {
  const [mutate] = useSetLessonAttendanceMutation();
  // Идентификаторы строк «в полёте» — для спиннера и защиты от двойного клика
  // по одному и тому же студенту.
  const [savingIds, setSavingIds] = useState<string[]>([]);

  const setAttendance = useCallback(
    async (attendanceId: string, attendance: boolean): Promise<boolean> => {
      setSavingIds((ids) => [...ids, attendanceId]);
      try {
        const { data } = await mutate({
          variables: { attendanceId: Number(attendanceId), attendance },
          optimisticResponse: {
            lmsSetAttendance: {
              __typename: 'LmsSetAttendanceMutation',
              success: true,
              customErrors: null,
              attendance: {
                __typename: 'LessonAttendancesType',
                id: attendanceId,
                attendance,
                source: 'manual',
                markedAt: new Date().toISOString(),
                markedByName: null,
              },
            },
          },
        });
        return Boolean(data?.lmsSetAttendance?.success);
      } catch {
        return false;
      } finally {
        setSavingIds((ids) => ids.filter((id) => id !== attendanceId));
      }
    },
    [mutate],
  );

  return { setAttendance, savingIds };
};
